/* eslint-disable react/no-unused-state */
// TODO: Clean up state.isAddMenuOpen
import {noop} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {ContextMenuOption} from 'components/ContextMenu';
import DataTableConfigContextHandler from 'notebook/data-tables/DataTableConfigContextHandler';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import {RegistrationTableConfig} from 'notebook/registration-tables/RegistrationTableConstants';
import ManageSourcesModal from 'notebook/structured-tables/components/modals/ManageSourcesModal';
import RefreshStructuredTableButton from 'notebook/structured-tables/components/RefreshStructuredTableButton';
import {SchemaFieldColumnUpdate} from 'notebook/structured-tables/StructuredTableConstants';
import {Table, TableData, TableHeaderControlsArgs} from 'notebook/tables/constants/TableConstants';
import {SelectionRange, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {
  handleRefreshDataTableColumns,
  mergeColumnUpdatesIntoColumnConstraints,
} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ModalAnchor from 'ui-library/ModalAnchor';
import {BasicEvent} from 'util/Types';
import {INPUT_SAMPLES_SOURCE_UUID} from 'workflows/WorkflowConstants';

interface DataTableConfigEditableProps extends MediocreSearchResultProps {
  dataTableConfig: DataTableConstants.DataTableConfig;
  updateHandler: (index: number, content: {item: DataTableConstants.DataTableConfig}) => void;
  // Required iff data table config is not standalone
  registrationTableConfigs: Array<RegistrationTableConfig>;
  cid: string;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  indentation: number | undefined;
  index: number;
  hideFormulaBox?: boolean;
  mouseDownHandler: (index: number, e: BasicEvent) => void;
  mouseOverHandler: (index: number, e: BasicEvent) => void;
  computedTable: Array<Array<EvaluatedCell>>;
  multiEditing: boolean;
  tableNamespace: string;
  showExcessLabels: boolean;
  metadataForRestore?: MetadataForRestore<TableIndex> | null;
}

interface State {
  isAddMenuOpen: boolean;
  isManageSourcesModalOpen: boolean;
}

/**
 * This is a legacy results table config, not a StructuredTable.
 *
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default class DataTableConfigEditable extends React.Component<DataTableConfigEditableProps, State> {
  override state: State = {
    isAddMenuOpen: false,
    isManageSourcesModalOpen: false,
  };

  static selectorsLink = {
    // selectors are not enabled, but TableEditable still requires this
    value: [],
    selectorUsageCounts: {},
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _handleUpdate = (index: number, {table, selection}: {table: TableData; selection?: any}): void => {
    this.props.updateHandler(this.props.index, {
      item: DataTableUtil.mergeTableUpdateIntoDataTableConfig(this.props.dataTableConfig, table),
    });
    if (selection) {
      this.props.editorProps.requestSelection(selection);
    }
  };

  _getTable(): Table {
    return DataTableUtil.convertDataTableConfigToTable(
      this.props.dataTableConfig,
      true /* indicateCustomColumns */
    );
  }

  _getExtraContextMenuOptions(): (selectionRange: SelectionRange) => Array<ContextMenuOption> | null {
    return DataTableUtil.getDataTableConfigContextMenuOptions(this.props.dataTableConfig, {
      onMarkAsReported: this.props.editor.props.locked ? null : this._markAsReported,
    });
  }

  _markAsReported = (selectionRange: SelectionRange, isReported: boolean): void => {
    this._handleUpdate(this.props.index, {
      table: DataTableUtil.markColumnAsReported(this._getTable(), selectionRange, isReported),
    });
  };

  _handleOpenDropdown(): void {
    this.setState({isAddMenuOpen: true});
  }

  _handleCloseDropdown(): void {
    this.setState({isAddMenuOpen: false});
  }

  _handleOpenManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: true});
  };

  _handleCloseManageSourcesModal = (): void => {
    this.setState({isManageSourcesModalOpen: false});
  };

  _handleSubmitManageSourcesModal = (
    existingSourceTableUuids: Array<string>,
    newSourceTableUuids: Array<string>
  ): void => {
    const sourceTableUuids = [...existingSourceTableUuids, ...newSourceTableUuids];
    const newDataTableConfig = reactUpdate(this.props.dataTableConfig, {
      isWatchingInputTable: {$set: sourceTableUuids.includes(INPUT_SAMPLES_SOURCE_UUID)},
      watchedRegistrationProvenanceUuids: {
        // eslint-disable-next-line no-restricted-syntax
        $set: _.without(sourceTableUuids, INPUT_SAMPLES_SOURCE_UUID),
      },
    });
    this.props.updateHandler(this.props.index, {item: newDataTableConfig});
    this._handleCloseManageSourcesModal();
  };

  _getNumWatchedSources(): number {
    const {isWatchingInputTable, watchedRegistrationProvenanceUuids} = this.props.dataTableConfig;
    const numWatchedInputTables = isWatchingInputTable ? 1 : 0;
    const numWatchedRegistrationTables = watchedRegistrationProvenanceUuids.length;
    return numWatchedInputTables + numWatchedRegistrationTables;
  }

  _renderManageSources(): JSX.Element | null {
    const numSources = this._getNumWatchedSources();
    if (
      this.props.dataTableConfig.isStandalone ||
      this.props.editorProps.viewer ||
      this.props.editorProps.locked
    ) {
      return null;
    }
    return (
      <Tooltip title="Manage sources">
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-left"
            icon="sources"
            onClick={this._handleOpenManageSourcesModal}
            size={ButtonSize.MINI}
            text={numSources.toString()}
          />
        </span>
      </Tooltip>
    );
  }

  _renderHeaderControls({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null {
    if (isCollapsed) {
      return null;
    }
    const doRefresh = async (): Promise<void> => {
      await handleRefreshDataTableColumns({
        entryOrTemplateId: this.props.editorProps.fileId,
        tableName: this.props.dataTableConfig.name,
        onUpdateTable: ({
          columnUpdates,
        }: {
          columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
        }) => {
          const table = this._getTable();
          const columnConstraints = mergeColumnUpdatesIntoColumnConstraints(
            table,
            columnUpdates,
            this.props.editorProps.getIdFromCid
          );
          const newTable = {...table, columnConstraints};
          this.props.updateHandler(this.props.index, {
            item: DataTableUtil.mergeTableUpdateIntoDataTableConfig(this.props.dataTableConfig, newTable),
          });
        },
      });
    };

    return (
      <span className="u-vertical-center-items">
        <RefreshStructuredTableButton
          entryOrTemplateId={this.props.editorProps.fileId}
          doRefresh={doRefresh}
          structuredTableKind="OLD Results table config"
          disabled={this.props.editorProps.locked}
        />

        {this._renderManageSources()}
      </span>
    );
  }

  override render(): JSX.Element {
    const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(
      this.props.editor,
      this.props.editorProps
    );
    return (
      <div>
        <ModalAnchor open={this.state.isManageSourcesModalOpen}>
          {this.state.isManageSourcesModalOpen && !this.props.dataTableConfig.isStandalone && (
            <ManageSourcesModal
              // @ts-expect-error: the link type should be correct
              linkType={this.props.dataTableConfig.linkType}
              initialSourceTableUuids={_.compact([
                this.props.dataTableConfig.isWatchingInputTable && INPUT_SAMPLES_SOURCE_UUID,
                ...this.props.dataTableConfig.watchedRegistrationProvenanceUuids,
              ])}
              onRequestClose={this._handleCloseManageSourcesModal}
              onRequestSubmit={this._handleSubmitManageSourcesModal}
              registrationTables={this.props.registrationTableConfigs}
            />
          )}
        </ModalAnchor>
        <DataTableConfigContextHandler
          entryId={this.props.editorProps.fileId}
          dataTableConfig={this.props.dataTableConfig}
          forRun={false}
        >
          {({additionalFunctions, insertableColumnInfos, getDisallowedColumnNames}) => (
            <TableEditable
              {...allEditorProps}
              table={this._getTable()}
              cid={this.props.cid}
              computedTable={this.props.computedTable}
              disableColumnLocking={false}
              disableFormulas={false}
              disableLegacyHeader={true}
              disableMaximizedTables={true}
              hasPlaceholderRows={false}
              indentation={this.props.indentation}
              index={this.props.index}
              isMaximized={false}
              additionalFunctions={additionalFunctions}
              headerControls={(args) => this._renderHeaderControls(args)}
              showFormulaBox={!this.props.hideFormulaBox}
              icon="table"
              insertableColumnInfos={insertableColumnInfos}
              disableMergingCells={true}
              mouseDownHandler={this.props.mouseDownHandler}
              mouseOverHandler={this.props.mouseOverHandler}
              tableLabel={MODEL_NAMES.Result}
              updateHandler={this._handleUpdate}
              isLeftAlignColumnHeader={true}
              getExtraContextMenuOptions={this._getExtraContextMenuOptions()}
              getDisallowedColumnNames={getDisallowedColumnNames}
              multiEditing={this.props.multiEditing}
              openManageSelectorsModal={noop}
              setMaximizedTable={noop}
              setSplitScreenTable={noop}
              selectorsEnabled={false}
              selectorsLink={DataTableConfigEditable.selectorsLink}
              showExcessLabels={this.props.showExcessLabels}
              tableNamespace={this.props.tableNamespace}
              metadataForRestore={this.props.metadataForRestore}
              {...forwardSearchResultProps(this.props)}
            />
          )}
        </DataTableConfigContextHandler>
      </div>
    );
  }
}
